import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, X, Flame } from "lucide-react";

const DISMISS_KEY = "mycoaching_daily_reminder_dismiss";

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function isDismissedToday() {
  return localStorage.getItem(DISMISS_KEY) === todayKey();
}

function dismissForToday() {
  localStorage.setItem(DISMISS_KEY, todayKey());
}

/** Show reminder from 9h if daily check-in not done */
function shouldShowReminder(doneToday) {
  if (doneToday || isDismissedToday()) return false;
  const hour = new Date().getHours();
  return hour >= 9;
}

export default function DailyCheckinReminderBanner({ onCheckinClick }) {
  const [visible, setVisible] = useState(false);
  const [streak, setStreak] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/checkins/daily/today").catch(() => ({ data: null })),
      api.get("/checkins/daily/streak").catch(() => ({ data: { streak: 0 } })),
    ]).then(([todayRes, streakRes]) => {
      setStreak(streakRes.data?.streak || 0);
      setVisible(shouldShowReminder(!!todayRes.data));
    });
  }, []);

  const handleDismiss = () => {
    dismissForToday();
    setVisible(false);
  };

  const handleAction = () => {
    if (onCheckinClick) {
      onCheckinClick();
    } else {
      navigate("/student/dashboard");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (!visible) return null;

  return (
    <div
      className="mt-6 flex items-start gap-3 p-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50"
      data-testid="daily-checkin-reminder"
    >
      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
        <Bell className="w-5 h-5 text-amber-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">N'oubliez pas votre check-in du jour</p>
        <p className="text-xs text-slate-500 mt-0.5">
          Prenez 30 secondes pour noter votre humeur et votre énergie.
          {streak > 0 && (
            <span className="inline-flex items-center gap-1 ml-1 text-orange-600 font-medium">
              <Flame className="w-3 h-3" /> {streak} jour{streak > 1 ? "s" : ""} de suite
            </span>
          )}
        </p>
        <Button
          size="sm"
          onClick={handleAction}
          className="mt-2 bg-amber-500 hover:bg-amber-600 text-white rounded-full h-8 text-xs"
        >
          Faire mon check-in
        </Button>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-amber-100/50"
        aria-label="Fermer"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}